import { forwardRef, memo, useCallback, useMemo, type ReactNode } from 'react';
import map from 'lodash/map';
import join from 'lodash/join';
import sortBy from 'lodash/sortBy';
import isUndefined from 'lodash/isUndefined';
import toSafeInteger from 'lodash/toSafeInteger';
// local imports
import { SkillLevel } from '../models/skillLevel';
import { ISkill } from '../models/skill';
import { injectParams } from '../helpers/path';
import StandardLink from './StandardLink';
import TruncatedTextLink, { type TruncatedTextVariant } from './TruncatedTextLink';

type SkillNameProps = {
  // React built-in
  readonly children?: ReactNode;
  // attributes
  readonly skill: ISkill;
  readonly level?: SkillLevel;
  readonly levels?: SkillLevel[];
  readonly onClick?: (skill: ISkill) => void;
  readonly skillPath?: string;
  readonly variant?: TruncatedTextVariant | 'h1' | 'h2' | 'h3' | 'h4' | 'inherit';
  readonly truncated?: boolean;
  readonly plain?: boolean;
  readonly bold?: boolean;
  readonly dark?: boolean;
  readonly maxLines?: number;
  readonly disabled?: boolean | null;
  readonly className?: string;
}

const SkillName = forwardRef<HTMLAnchorElement, SkillNameProps>(({
  children,
  skill,
  level,
  levels,
  onClick,
  skillPath,
  variant,
  truncated = false,
  maxLines,
  disabled = false,
  plain = false,
  bold = false,
  dark = false,
  className
}, ref) => {
  const { abbr, title } = skill || {};

  const handleClick = useCallback(() => {
    if (onClick && skill && skill.id) onClick(skill);
  }, [onClick, skill]);

  const link = useMemo(() => skillPath ? injectParams(skillPath, { skill_id: abbr }) : undefined, [abbr, skillPath]);

  const suffix = useMemo(() => !levels && isUndefined(level) ? undefined : (
    <>
      {' - '}
      {levels ? join(sortBy(map(levels, toSafeInteger)), ',') : toSafeInteger(level)}
    </>
  ), [level, levels]);

  return (truncated || maxLines) && !children ? (
    <TruncatedTextLink
        ref={ref}
        variant={variant}
        plain={plain}
        bold={bold}
        dark={dark}
        text={title}
        suffix={suffix}
        maxLines={maxLines}
        to={disabled ? undefined : link}
        onClick={!disabled && onClick ? handleClick : undefined}
        className={className}
    />
  ) : (
    <StandardLink
        ref={ref}
        variant={variant}
        bold={bold}
        dark={dark}
        to={disabled ? undefined : link}
        onClick={!disabled && onClick ? handleClick : undefined}
        className={className}
    >
      {children || title}
      {suffix}
    </StandardLink>
  );
});

SkillName.displayName = 'SkillName';

export default memo(SkillName);
